import { useTranslation } from 'next-i18next'
import OperatorPromoNote from 'src/components/OperatorPromo/OperatorPromoNote'
import PromoBadge, { promoBadgeContent } from 'src/components/Promo/PromoBadge'
import { useGetPrice } from 'src/hooks/usePrice'
import { useListingVariant } from 'src/lib/listingVariants'
import type { IConfiguration, ILocation } from 'src/types/graphql'
import { numberRounedUpWithoutDecimals } from 'src/utils'
import { getDiscount } from 'src/utils/coverPhoto'
import { operatorPromoPill } from 'src/utils/operatorPromo'
import { getActiveScarcityCount } from './utils'

const IVA_RATE = 0.16

interface ListingPriceFactsProps {
  location: ILocation
  activeConfig: IConfiguration
  /** A multi-size listing already shows the selected size's promo and
   * scarcity in its «Espacio seleccionado» card — only the totals line is
   * added then, never a second badge. */
  hasConfigSelector: boolean
}

/**
 * MOBILE-ONLY price facts under the listing title (Daniel, 2026-10-27:
 * «reducir #s y agregar promo en rojo … en el título la info y abajo menos
 * info, no eliminar nada»).
 *
 * The mobile sticky bar keeps two lines — price + one meta line — so every
 * fact it used to stack (IVA-inclusive 1er-mes total, the 2°-mes price,
 * «Último disponible», «Mín. N meses», units available, the operator's full
 * promo) is stated here instead, once, where there is room. Desktop's price
 * rail shows all of them next to the title already, so this is `md:hidden`.
 */
export default function ListingPriceFacts({
  location,
  activeConfig,
  hasConfigSelector
}: ListingPriceFactsProps) {
  const { t } = useTranslation('details')
  const { getPrice } = useGetPrice()
  const variant = useListingVariant()
  const variantKey = variant.meta.variantKey
  const isBrokerMode = variantKey === 'broker'
  const isTraditionalMode = variantKey === 'traditional'

  const price = getPrice(location, activeConfig)
  const discount = getDiscount(activeConfig, isBrokerMode)
  const operatorPromo = activeConfig.operatorPromo
  const badge = promoBadgeContent(discount, operatorPromoPill(operatorPromo))
  const priceIncludesIva =
    isTraditionalMode && location.listPriceIncludesIva === true

  const availability = activeConfig.availability ?? 0
  const availabilityIsMultiple = availability > 1 && !isBrokerMode
  const { showScarcity, scarcityCount } = getActiveScarcityCount(
    activeConfig,
    isBrokerMode,
    location.inventoryIsFresh
  )
  const showLastUnit =
    variant.priceCard.showScarcity &&
    (variantKey === 'monthly' || variantKey === 'traditional') &&
    showScarcity &&
    !availabilityIsMultiple
  const minMonths =
    !isBrokerMode && (activeConfig.minMonths ?? 0) > 1
      ? activeConfig.minMonths
      : null

  // The totals the sticky bar no longer spells out. Only a promo needs them
  // here: without one the bar's second line IS «+ IVA = $X».
  const totalsLine = discount
    ? t('PRICE_FACTS.FIRST_MONTH_TOTALS', {
        minMonths: discount.minMonths,
        firstMonth: numberRounedUpWithoutDecimals(
          price * (1 - discount.percentage / 100) * (1 + IVA_RATE)
        ),
        regular: numberRounedUpWithoutDecimals(price * (1 + IVA_RATE))
      })
    : operatorPromo
      ? priceIncludesIva
        ? t('PRICE_FACTS.LIST_IVA_INCLUDED')
        : t('PRICE_FACTS.LIST_TOTAL', {
            total: numberRounedUpWithoutDecimals(price * (1 + IVA_RATE))
          })
      : null

  // A multi-size listing's «Espacio seleccionado» card already carries the
  // promo pill, scarcity and units for the active size — only the minimum
  // stay (shown nowhere else on mobile) is added for it here.
  const ownsCardFacts = !hasConfigSelector
  const showPromoPill = ownsCardFacts && Boolean(badge) && !operatorPromo
  const showLast = ownsCardFacts && showLastUnit
  const showUnits = ownsCardFacts && availabilityIsMultiple
  const showBadgeRow = showPromoPill || showLast || showUnits || !!minMonths
  const showOperatorNote = ownsCardFacts && Boolean(operatorPromo)

  if (!showBadgeRow && !totalsLine && !showOperatorNote) return null

  return (
    <div className="mt-3 flex flex-col gap-2 md:hidden">
      {showBadgeRow && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {/* The operator's promo gets its full labelled note below instead
              of a pill — it is the operator's offer, never SpotMe's. */}
          {showPromoPill && badge && (
            <PromoBadge promo={badge} placement="inline" />
          )}
          {showLast && (
            <span className="inline-flex items-center gap-1.5 font-display text-[10px] tracking-[0.14em] text-blue-500 uppercase">
              <span
                aria-hidden="true"
                className="inline-flex size-1 rounded-full bg-blue-500"
              />
              {scarcityCount === 1
                ? t('SCARCITY.MONTHLY_LAST')
                : t('SCARCITY.MONTHLY_REMAINING', { count: scarcityCount })}
            </span>
          )}
          {showUnits && (
            <span className="font-display text-[10px] tracking-[0.14em] text-ink-200 uppercase">
              {t('SECTIONS.SPACES_AVAILABLE', { count: availability })}
            </span>
          )}
          {minMonths && (
            <span className="inline-flex items-center gap-1.5 font-display text-[10px] tracking-[0.14em] text-ink-200 uppercase">
              <i aria-hidden="true" className="si si-hours text-[11px]" />
              {t('PRICE_CARD.MIN_MONTHS', { count: minMonths })}
            </span>
          )}
        </div>
      )}
      {showOperatorNote && <OperatorPromoNote promo={operatorPromo} />}
      {totalsLine && (
        <p className="text-[12px] leading-snug text-ink-200">{totalsLine}</p>
      )}
    </div>
  )
}
